import React, { useEffect, useState } from 'react'

import { PluckingRooster as PluckingRoosterGame, ASSETS as ROOSTER_ASSETS } from './PluckingRooster/PluckingRooster'
import { SubmitGame as BLSubmitGame, ValidatePlayer as BLValidatePlayer, TrackMetric as BLTrackMetric, GetSettings as BLGetSettings } from './../../../utilities/BL/Admin/Games/PluckingRooster'

import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'
import { ValidateSqlInjection as BLValidateSqlInjection } from './../../../utilities/BL/Security'

const SOURCE_MAPPING = {
    'obs': 'OBS Streaming',
    'screen': 'Pantalla Estadio',
    'qr': 'Código QR'
};

const TERMS_TEXT = `TÉRMINOS Y CONDICIONES DE LA DINÁMICA
1. Organizador de la dinámica: Dorados de Sinaloa es el único responsable organizador de esta dinámica y de la entrega física del premio.
2. Deslinde de responsabilidad técnica: Five Digital Minds e Interaktia actúan únicamente como los proveedores tecnológicos de la plataforma del juego y queda eximido de cualquier responsabilidad derivada de la entrega de premios o controversias de la dinámica.
3. Participantes: Dinámica válida únicamente para aficionados presentes en el estadio que sean mayores de edad.
4. Registro: Cada participante puede registrarse una sola vez. Intentos múltiples serán anulados.
5. Elección del ganador: Los ganadores se elegirán con base en la puntuación más alta de aciertos y, en caso de empate, por el menor tiempo de registro o participación.
6. Decisiones: Las decisiones del comité organizador son inapelables y definitivas.`;

const PRIVACY_TEXT = `AVISO DE PRIVACIDAD SIMPLIFICADO
Sus datos personales (Nombre Completo, Correo Electrónico y Teléfono) serán recabados con la única finalidad de registrar su participación en la dinámica, poder contactarle y validar su identidad en caso de resultar ganador, así como para estadísticas internas de participación de los organizadores.

Al aceptar este aviso, autoriza expresamente que sus datos de contacto sean compartidos y transferidos entre los patrocinadores, el Dorados de Sinaloa y la empresa operadora Five Digital Minds, así como sus asociados comerciales y aliados estratégicos con fines de marketing, estadísticas y entrega de premios. No se realizarán transferencias a otros terceros sin su consentimiento. Para dudas o aclaraciones contacte a soporte.`;

const PluckingRooster = () => {
    const { showMessage } = useFloatMessage();

    // Estado del flujo
    const [step, setStep] = useState('register'); // register, game, sending, done, played, resendPoints, inactive
    const [userData, setUserData] = useState({
        fullName: '',
        email: '',
        phoneNumber: '',
        companionType: '' // Valor por defecto vacío para obligar a seleccionar
    });
    const [finalScore, setFinalScore] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [formStartTracked, setFormStartTracked] = useState(false);

    // Estados para Términos, Aviso de Privacidad y Modales legales
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // 1. Manejo de Sesión y Métricas
    const getSessionId = () => {
        let sessId = sessionStorage.getItem('rooster_session_id');
        if (!sessId) {
            sessId = 'sess_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
            sessionStorage.setItem('rooster_session_id', sessId);
        }
        return sessId;
    };

    const trackEvent = async (eventName) => {
        const sessId = getSessionId();
        const urlParams = new URLSearchParams(window.location.search);
        const rawSrc = urlParams.get('s') || urlParams.get('utm_medium') || urlParams.get('src') || 'direct';
        const srcVal = SOURCE_MAPPING[rawSrc] || rawSrc;

        try {
            await BLTrackMetric(eventName, sessId, srcVal);
        } catch (e) {
            console.error('Local tracking error:', e);
        }

        try {
            if (window.gtag) {
                window.gtag('event', `rooster_${eventName}`, {
                    session_id: sessId,
                    event_category: 'rooster_game',
                    event_label: eventName,
                    traffic_source: srcVal
                });
            }
        } catch (e) {
            console.error('GA4 tracking error:', e);
        }
    };

    const handleInputFocus = () => {
        if (!formStartTracked) {
            setFormStartTracked(true);
            trackEvent('form_start');
        }
    };

    const handleInputChange = (e) => {
        setUserData({ ...userData, [e.target.name]: e.target.value });
    };

    // VALIDACIÓN AL CARGAR (LocalStorage y métricas iniciales) y PRECARGA DE RECURSOS
    useEffect(() => {
        // Precarga de imágenes y recursos del juego en segundo plano mientras el usuario se registra
        if (ROOSTER_ASSETS) {
            Object.values(ROOSTER_ASSETS).forEach((src) => {
                if (typeof src === 'string') {
                    if (src.endsWith('.png') || src.endsWith('.jpg') || src.endsWith('.jpeg') || src.endsWith('.webp') || src.endsWith('.gif')) {
                        const img = new Image();
                        img.src = src;
                    } else if (src.endsWith('.mp3') || src.endsWith('.wav') || src.endsWith('.ogg')) {
                        const audio = new Audio();
                        audio.src = src;
                        audio.preload = 'auto';
                    }
                }
            });
        }
        const bannerImg = new Image();
        bannerImg.src = '/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif';

        // const played = localStorage.getItem('rooster_played');

        // if (played === 'true') {
        //     setStep('played');
        // }

        const trackInitialEventsAndCheckActive = async () => {
            const settingsRes = await BLGetSettings();
            if (settingsRes.success && settingsRes.data) {
                const isGameActive = settingsRes.data.isGameActive ?? true;
                if (!isGameActive) {
                    setStep('inactive');
                    return;
                }
            }

            const hasPageView = sessionStorage.getItem('rooster_page_view_tracked');
            if (!hasPageView) {
                sessionStorage.setItem('rooster_page_view_tracked', 'true');
                await trackEvent('page_view');
            }

            const urlParams = new URLSearchParams(window.location.search);
            const source = urlParams.get('s') || urlParams.get('utm_medium') || urlParams.get('src');
            if (source === 'obs' || source === 'screen' || source === 'qr') {
                const hasQrScan = sessionStorage.getItem('rooster_qr_scan_tracked');
                if (!hasQrScan) {
                    sessionStorage.setItem('rooster_qr_scan_tracked', 'true');
                    await trackEvent('qr_scan');
                }
            }
        };
        trackInitialEventsAndCheckActive();

        return () => {
            const gameContainer = document.getElementById('PluckingRooster__Game');
            if (gameContainer) gameContainer.style.overflow = 'visible';
        }
    }, []);

    const handleRegister = async (e) => {
        e.preventDefault();
        
        // 1. Validar campos vacíos y formatos (Regex)
        if (!userData.fullName.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `El nombre completo es requerido.` });
            return;
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!userData.email.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `El correo electrónico es requerido.` });
            return;
        } else if (!emailRegex.test(userData.email.trim())) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `El correo electrónico no tiene un formato válido.` });
            return;
        }

        const phoneRegex = /^\+?[0-9]{10,15}$/;
        if (!userData.phoneNumber.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `El teléfono / WhatsApp es requerido.` });
            return;
        } else if (!phoneRegex.test(userData.phoneNumber.trim())) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `El teléfono debe ser numérico y contener entre 10 y 15 dígitos.` });
            return;
        }

        // if (!userData.companionType) {
        //     showMessage({ type: MESSAGE_TYPE.BAD, text: `Por favor, selecciona tu zona para jugar.` });
        //     return;
        // }

        if (!acceptedTerms) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `Debes aceptar los Términos y Condiciones y el Aviso de Privacidad para jugar.` });
            return;
        }

        setIsLoading(true);

        // 2. Validación de Inyección SQL
        const isNameThreat = await BLValidateSqlInjection(userData.fullName);
        const isEmailThreat = await BLValidateSqlInjection(userData.email);
        const isPhoneThreat = await BLValidateSqlInjection(userData.phoneNumber);

        if (isNameThreat || isEmailThreat || isPhoneThreat) {
            setIsLoading(false);
            showMessage({ type: MESSAGE_TYPE.BAD, text: `Caracteres no permitidos en el formulario.` });
            return;
        }

        // 3. VALIDAMOS CONTRA EL BACKEND
        const validation = await BLValidatePlayer(userData.email.trim(), userData.phoneNumber.trim());

        setIsLoading(false);

        if (!validation.success) {
            setStep('played');
            return;
        }

        const gameContainer = document.getElementById('PluckingRooster__Game');
        if (gameContainer) gameContainer.style.overflow = 'hidden';
        
        // Registrar inicio de dinámica exitoso
        await trackEvent('form_complete');
        await trackEvent('game_start');
        setStep('game');
    };
    
    // 2. Callback cuando el juego termina
    const handleGameFinish = async (score) => {
        await new Promise(resolve => setTimeout(resolve, 3000));

        localStorage.setItem('rooster_played', 'true');

        const gameContainer = document.getElementById('PluckingRooster__Game');
        if (gameContainer) gameContainer.style.overflow = 'visible';

        setFinalScore(score);
        setStep('sending');

        const result = await BLSubmitGame(userData, score, acceptedTerms);

        if (result.success) {
            await trackEvent('game_complete');
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Puntaje enviado con éxito` });
            setStep('done');
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha habido un error al intentar enviar tu puntaje. Inténtalo nuevamente.' });
            setStep('resendPoints'); 
        }
    };

    const ResendPoints = async () => {
        localStorage.setItem('rooster_played', 'true');

        const gameContainer = document.getElementById('PluckingRooster__Game');
        if (gameContainer) gameContainer.style.overflow = 'visible';

        setStep('sending');

        const result = await BLSubmitGame(userData, finalScore, acceptedTerms);

        if (result.success) {
            await trackEvent('game_complete');
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Puntuación enviada con éxito` });
            setStep('done');
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: '¡Ups! Ha habido un error al intentar enviar tu puntaje' });
            setStep('resendPoints'); 
        }
    };

    const renderLegalModal = (title, content, setter) => {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-75 d-flex align-items-center justify-content-center px-3" style={{ zIndex: 11000 }}>
                <div className="card w-100 bg-white border-0 rounded-4 shadow-lg p-4" style={{ maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-dark mb-0 fs-5">{title}</h4>
                        <button type="button" className="btn-close" onClick={() => setter(false)}></button>
                    </div>
                    <div className="text-secondary small" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify', lineHeight: '1.5' }}>
                        {content}
                    </div>
                    <button type="button" className="btn btn-danger w-100 py-2 rounded-3 fw-bold mt-4" onClick={() => setter(false)}>
                        Cerrar
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div id='PluckingRooster__Game' style={{ width: '100vw', height: '100vh' }}>
            {showTermsModal && renderLegalModal('Términos y Condiciones', TERMS_TEXT, setShowTermsModal)}
            {showPrivacyModal && renderLegalModal('Aviso de Privacidad', PRIVACY_TEXT, setShowPrivacyModal)}

            {step === 'register' && (
                <>
                    <div className="container flex flex-col justify-center h-full bg-gray-100 pb-5"> 
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia" alt="Logo" />
                        <p className="SelfieFan__Fan__Title">¡Bienvenid@ Aficionad@!</p>
                        {/* <p className="SelfieFan__Fan__Text--Center">Participa por un regalo especial.</p> */}
                        <p className="SelfieFan__Fan__Text--Center">Juega con nosotros.</p>
                        <p className='Messages__Fan__Text--Center'>Obtén puntos cada vez que le pegues al pirata</p>
                        {/* <p className='Messages__Fan__Text--Center'>La persona que obtenga más puntos y más rápido será ganador(a)</p> */}

                        <hr />

                        <p className='SelfieFan__Fan__Title mt-4'>¡Únete al juego!</p>

                        <label className='PluckingRooster__Instructions__Form__Labels' htmlFor='fullName'>Escribe tu nombre completo</label>
                        <input id="fullName" name="fullName" placeholder="Escribe tu nombre" onChange={handleInputChange} onFocus={handleInputFocus} className="PluckingRooster__Instructions__Form__Inputs" />

                        <label className='PluckingRooster__Instructions__Form__Labels' htmlFor='email'>Correo electrónico</label>
                        <input id="email" name="email" placeholder="Escribe tu correo" onChange={handleInputChange} onFocus={handleInputFocus} className="PluckingRooster__Instructions__Form__Inputs" />

                        <label className='PluckingRooster__Instructions__Form__Labels' htmlFor='phoneNumber'>WhatsApp o Teléfono</label>
                        <input id="phoneNumber" name="phoneNumber" placeholder="Ej. 6691234567" onChange={handleInputChange} onFocus={handleInputFocus} className="PluckingRooster__Instructions__Form__Inputs" />

                        {/* <label className='PluckingRooster__Instructions__Form__Labels' htmlFor='companionType'>Selecciona tu zona</label>
                        <select name="companionType" value={userData.companionType} onChange={handleInputChange} className='PluckingRooster__Instructions__Form__List'>
                            <option value="">Elige tu zona</option>
                            <option value="azul">Zona Azul</option>
                            <option value="oro">Zona Oro</option>
                        </select> */}

                        <div className="form-check my-3 text-start d-flex align-items-start justify-content-center gap-2">
                            <input 
                                className="form-check-input mt-1"
                                type="checkbox" 
                                id="acceptTermsCheck" 
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer', flexShrink: 0 }}
                            />
                            <label className="form-check-label text-dark" htmlFor="acceptTermsCheck" style={{ fontSize: '0.75rem', lineHeight: '1.4', cursor: 'pointer' }}>
                                He leído y acepto los <button type="button" className="btn btn-link p-0 text-danger small border-0 align-baseline" style={{ fontSize: '0.75rem', fontWeight: 'bold' }} onClick={() => setShowTermsModal(true)}>Términos y Condiciones</button> y el <button type="button" className="btn btn-link p-0 text-danger small border-0 align-baseline" style={{ fontSize: '0.75rem', fontWeight: 'bold' }} onClick={() => setShowPrivacyModal(true)}>Aviso de Privacidad</button> de la dinámica.
                            </label>
                        </div>

                        {isLoading ? (
                            <button className="Btn__Adsl--RedOutline PluckingRooster__Instructions__Form__Button" disabled={true}>Iniciando juego...</button>
                        ) : (
                            <button onClick={handleRegister} className="Btn__Adsl--Red PluckingRooster__Instructions__Form__Button">Jugar ahora</button>
                        )}
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}

            {step === 'game' && (
                <PluckingRoosterGame onGameFinish={handleGameFinish} initialDuration={15} />
            )}

            {step === 'sending' && (
                <>
                    <div className="container flex flex-col justify-center h-full bg-gray-100 pb-5">
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="SelfieFan__Fan__Title mt-3">¡Muy bien{(userData && userData?.fullName && userData.fullName !== '') && ` ${userData.fullName}`}!</p>
                        <p className="SelfieFan__Fan__Text--Center">Un momento... estamos guardando tu puntuación.</p>
                        <p className="SelfieFan__Fan__Text--Center">{finalScore} puntos</p>
                        <p className="SelfieFan__Fan__Text--Center">No salgas de esta pantalla.</p>

                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}

            {step === 'done' && (
                <>
                    <div className="SelfieFan__Sent container">
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-success"><i className="bi bi-check-circle-fill"></i></p>
                        <p className="SelfieFan__Sent__Title mb-0">¡Muy bien!</p>
                        {/* <p className='Messages__Fan__Text--Center mb-0'>Has logrado {finalScore} puntos</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Muchas gracias por participar</p> */}
                        <p className='Messages__Fan__Text--Center mb-0'>Tu puntaje ha sido enviado correctamente</p>
                        <p className='Messages__Fan__Text--Center mb-0'>¡{finalScore} puntos!</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Muchas gracias por jugar y seguir apoyando</p>
                        {/* <p className='Messages__Fan__Text--Center mb-0'>Podrás ir viendo en la pantalla del estadio el ranking para saber si has sido ganador(a)</p> */}
                        {/* <p className='Messages__Fan__Text--Center mb-0'>Ojo: en caso de resultar ganador(a), nos comunicaremos contigo.</p> */}
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}

            {step === 'played' && (
                <>
                    <div className="SelfieFan__Sent container">
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-success"><i className="bi bi-check-circle-fill"></i></p>
                        <p className="SelfieFan__Sent__Title mt-3 mb-0">Gracias por tu participación</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Tu puntaje fue enviado correctamente</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Muchas gracias por jugar y seguir apoyando</p>
                        {/* <p className='Messages__Fan__Text--Center mb-0'>Ojo: en caso de resultar ganador(a), nos comunicaremos contigo.</p> */}
                        {/* <p className='Messages__Fan__Text--Center mb-0'>Podrás ir viendo en la pantalla del estadio el ranking para saber si has sido ganador(a)</p> */}
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}

            {step === 'resendPoints' && (
                <>
                    <div className="SelfieFan__Sent container">
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-danger"><i className="bi bi-x-circle-fill"></i></p>
                        <p className="mb-0">No ha sido posible enviar tu puntaje. Inténtalo nuevamente con el botón Enviar mi puntaje</p>
                        <button type='button' className='Btn__Adsl--RedOutlineTransparent mx-auto' onClick={() => ResendPoints()}>Enviar mi puntaje</button>
                        {/* <p className="mb-0">No ha sido posible guardar tu puntaje. Inténtalo nuevamente con el botón Guardar mi puntaje</p>
                        <button type='button' className='Btn__Adsl--RedOutlineTransparent mx-auto' onClick={() => ResendPoints()}>Guardar mi puntaje</button> */}
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fcm_doradosfc' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}

            {step === 'inactive' && (
                <>
                    <div className="SelfieFan__Sent container">
                        <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                        <p className="fs-1 mb-0 text-danger"><i className="bi bi-check-circle-fill text-danger"></i></p>
                        <p className="SelfieFan__Sent__Title mb-0 text-danger">Dinámica finalizada</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Gracias por tu interés y participación. Se han cerrado los registros.</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Te invitamos a participar en las siguientes dinámicas que tendremos para ti.</p>
                        {/* <p className='Messages__Fan__Text--Center mb-0'>El registro y acceso al juego se encuentran temporalmente cerrados.</p>
                        <p className='Messages__Fan__Text--Center mb-0'>Por favor, espera indicaciones en la pantalla del estadio.</p> */}
                        
                        <hr />

                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='SelfieFan__Sent__Banner'>
                            <img src='/img/advertising/fdm/banner/five-digital-minds-agradecimiento.gif' alt='Publicidad' className='d-block m-auto SelfieFan__Sent__Banner__Image' />
                        </a>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center d-block'>Contactar ahora</a>

                        <hr />

                        <p className='text-center mb-0'>¿Quieres conocer más de esta tecnología?</p>
                        <p className='text-center mb-0'>Interaktia está listo para ti</p>
                        <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=banner_text&utm_campaign=fcm_doradosfc' target='_blank' className='Btn__Adsl--RedOutline m-auto text-decoration-none text-center'>Contactar ahora</a>
                    </div>

                    <footer className="text-center mt-3 FooterCredits">
                        Hecho con ♥ por <a href='https://interaktia.com/?utm_source=rooster_web&utm_medium=footer&utm_campaign=fcm_doradosfc' target='_blank' rel="noreferrer" className='text-dark'>Interaktia</a> | 2026
                    </footer>
                </>
            )}
        </div>
    );
};

export { PluckingRooster };